import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ShieldAlert, Upload, X, Image as ImageIcon, IndianRupee } from "lucide-react";

const REASONS = [
  { value: "damaged", label: "Damaged in transit" },
  { value: "lost", label: "Lost / never arrived" },
  { value: "tampered", label: "Tampered package" },
  { value: "partial", label: "Partial delivery / missing items" },
];

async function toBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function ReportDamageDialog({ open, onClose, order }) {
  const [reason, setReason] = useState("damaged");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason("damaged"); setDescription(""); setAmount(""); setPhotos([]);
    }
  }, [open]);

  const handleFiles = async (files) => {
    const arr = Array.from(files || []);
    const room = 4 - photos.length;
    const next = arr.slice(0, room);
    if (arr.length > room) toast.info(`Only ${room} more photo(s) allowed — max 4`);
    const encoded = [];
    for (const f of next) {
      if (f.size > 3_500_000) { toast.error(`${f.name} too large (max 3.5 MB)`); continue; }
      encoded.push(await toBase64(f));
    }
    setPhotos([...photos, ...encoded]);
  };

  const submit = async () => {
    if (!description.trim()) { toast.error("Describe what happened"); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setLoading(true);
    try {
      await api.post("/damage/claims", {
        tracking_id: order.tracking_id,
        reason, description: description.trim(),
        amount_requested: amt,
        photos_base64: photos,
      });
      toast.success("Claim filed! Our team will review within 48 hours.");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to file claim");
    } finally { setLoading(false); }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !loading && onClose()}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="report-damage-dialog">
        <DialogHeader className="p-5 border-b border-slate-100 shrink-0">
          <DialogTitle className="font-['Outfit'] text-lg font-bold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-red-500" /> Report Damage / Claim
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">For order <span className="font-mono font-bold text-[#FF6B00]">{order.tracking_id}</span> · Order total ₹{order.price?.total ?? order.price?.final_price ?? "—"}</p>
        </DialogHeader>

        <div className="p-5 overflow-y-auto space-y-4 flex-1">
          <div>
            <Label className="text-xs">What happened?</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              {REASONS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setReason(r.value)}
                  className={`p-2.5 rounded-xl border-2 text-xs font-semibold text-left transition ${reason === r.value ? "border-[#FF6B00] bg-orange-50 text-[#FF6B00]" : "border-slate-200 text-slate-600"}`}
                  data-testid={`reason-${r.value}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Describe the issue</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full mt-1 rounded-xl border border-slate-200 p-3 text-sm h-24 resize-none focus:border-[#FF6B00] focus:outline-none"
              placeholder="e.g., Package arrived with broken glass, 2 out of 5 items damaged..."
              maxLength={2000}
              data-testid="damage-description"
            />
          </div>

          <div>
            <Label className="text-xs">Refund amount requested (₹)</Label>
            <div className="relative mt-1">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g., 500" className="h-10 pl-9" data-testid="damage-amount" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Evidence photos (up to 4)</Label>
            <div className="grid grid-cols-4 gap-2 mt-1.5">
              {photos.map((p, i) => (
                <div key={`${p.slice(0, 40)}-${i}`} className="relative aspect-square rounded-xl border-2 border-emerald-200 overflow-hidden" data-testid={`uploaded-photo-${i}`}>
                  <img src={p} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="h-3 w-3" /></button>
                </div>
              ))}
              {photos.length < 4 && (
                <label className="aspect-square rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center cursor-pointer hover:border-[#FF6B00] transition" data-testid="photo-upload-tile">
                  <Upload className="h-5 w-5 text-slate-400" />
                  <span className="text-[10px] text-slate-400 mt-1">Add</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
                </label>
              )}
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5">Max 3.5 MB per photo. Clear images help faster approval.</p>
          </div>
        </div>

        <div className="p-5 border-t border-slate-100 flex gap-2 shrink-0">
          <Button variant="outline" onClick={onClose} disabled={loading} className="flex-1 rounded-xl">Cancel</Button>
          <Button onClick={submit} disabled={loading} className="flex-1 bg-red-600 hover:bg-red-700 rounded-xl text-white" data-testid="submit-damage-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldAlert className="h-4 w-4 mr-1.5" /> File Claim</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
